import Button from '@/components/Button';
import Breadcrumbs from '@/componentsDS/Breadcrumbs/index.jsx';
import Stepper from '@/componentsDS/Stepper/Stepper';
import Search from '@/componentsDS/Search/index';
import FloatingPanel from '@/componentsDS/FloatingPanel/FloatingPanel.jsx';
import { FloatingPanelType } from '@/componentsDS/FloatingPanel/FloatingPanel.type.js';
import EFormRenderer from '@/components/EFormV2/EFormRenderer';
import Loader from '@/componentsDS/Loader/Loader';
import SelectorBox2 from '@/components/SelectorBox2';
import Callout from '@/componentsDS/Callout/Callout';
import { CalloutType, CalloutVariant } from '@/componentsDS/Callout/Callout.type';
import Modal from '@/componentsDS/Modal/Modal';
import CardConfigure from '@/componentsDS/Modal/CardConfigure';
import useModal from '@/hooks/use-modal/index';
import AccordionCard from '@/components/AccordionCard';
import { buildLogEntries } from './utils/build-log-entries';
import SubmittalBanner from './components/SubmittalBanner';
import ActivityLogModal from '@/componentsDS/ActivityLogModal';
import { ReactComponent as IconInfo } from '@/assets/svgs/DesignSystem/DS_Icon_info.svg';
import { ToastContext } from '@/context/toast/index.js';
import usePage from '@/hooks/use-page';
import useStepList from '@/hooks/use-step-list/index';
import useConfig from '@/hooks/use-config/index';
import useAllowedReceiverCheck from '@/hooks/use-submittals/useAllowedReceiverCheck';
import useSubmittalsDataElements from '@/hooks/use-submittals/useSubmittalsDataElements';
import useSubmittalsVisibility from '@/hooks/use-submittals/useSubmittalsVisibility';
import useSubmittalForm from '@/hooks/use-submittals/useSubmittalForm';
import useSubmittalActions from '@/hooks/use-submittals/useSubmittalActions';
import useCopyFromLast from '@/hooks/use-submittals/useCopyFromLast';
import { useLocation, useNavigate, useParams } from '@/lib/router-dom';
import submittalsService from '@/services/api/submittalsService.js';
import { getAutoFilledPaths, getCopyFromLastSections, getEditableHighlightPaths, getLockedPaths } from '@/utils/eform-schema-paths';
import { orderBy } from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { NON_EDITABLE_STATUSES, SUBMITTAL_STATUS } from './constants';

const STEPS = [
    { stepTitle: 'Choose Data Element' },
    { stepTitle: 'Fill E-Form' },
];

const CreateSubmissionPage = () => {
    const { dataElements, isSubmittalsEnabled } = useSubmittalsDataElements();
    const { showSubmittals } = useSubmittalsVisibility();
    const { provideItems } = useConfig();
    const { checkAllowedReceiver, configProducesReady } = useAllowedReceiverCheck();
    const { addToastMessage } = useContext(ToastContext);
    const { goTo } = useNavigate();
    const { currentState: locationState } = useLocation();
    const params = useParams();
    const submittalId = params?.submittal_id;
    const isEditMode = !!submittalId;
    const currentUserId = useSelector(state => state.auth.user?.id);

    const { step, goToStep, prevStep, nextStep } = useStepList(STEPS);

    const [selectedDataElement, setSelectedDataElement] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [existingSubmittal, setExistingSubmittal] = useState(null);
    const [activityLogs, setActivityLogs] = useState([]);

    const floatingPanelRef = useRef(null);

    const {
        schema,
        payload,
        setPayload,
        isDirty,
        setIsDirty,
        setIsValidFormat,
        errorListToDisplay,
        isSubmitClicked,
        setIsSubmitClicked,
        validateSchema,
        validateErrors,
    } = useSubmittalForm({ selectedDataElement, existingSubmittal });

    const {
        isSaving,
        isSubmitting,
        handleSaveDraft,
        handleSubmit,
        handleUpdateApprover,
    } = useSubmittalActions({
        submittalId,
        isEditMode,
        selectedDataElement,
        payload,
        addToastMessage,
        validateSchema,
        validateErrors,
        setIsDirty,
        setIsSubmitClicked,
        floatingPanelRef,
    });

    const onCopyFromLast = useCopyFromLast({
        selectedDataElement,
        schema,
        payload,
        setPayload,
        addToastMessage,
    });

    const { breadcrumbsItems } = usePage({
        title: isEditMode ? 'Update Submission' : 'Create New Submission',
        breadcrumbs: [
            { label: '', link: '/', isHomePage: true },
            { label: 'Submittals', link: '/submittals' },
            { label: isEditMode ? 'Update Submission' : 'Create New Submission' },
        ],
    });

    const { visible: isCancelModalVisible, show: showCancelModal, hide: hideCancelModal } = useModal();
    const { visible: isActivityLogVisible, show: showActivityLog, hide: hideActivityLog } = useModal();

    // Load the existing submittal when editing, then resolve its data element so
    // the form hook can fetch the schema and seed the payload.
    useEffect(() => {
        if (!isEditMode) return;
        submittalsService.getSubmittalById(submittalId).then(([err, data]) => {
            if (err) {
                addToastMessage(err, 'error');
                goTo('/submittals');
                return;
            }
            if (!data?.created_by || data.created_by !== currentUserId || NON_EDITABLE_STATUSES.has(data?.status)) {
                addToastMessage('You are not allowed to edit this submittal.', 'error');
                goTo(`/submittals/${submittalId}`);
                return;
            }
            setExistingSubmittal(data);
            const de = provideItems.find(d => d.id === data.data_element_id);
            if (de) setSelectedDataElement(de);
        });
        submittalsService.getActivityLogs(submittalId).then(([err, logs]) => {
            if (!err && logs) setActivityLogs(logs);
        });
    }, [isEditMode, submittalId, provideItems, currentUserId]);

    // In edit mode, skip step 1 once the schema has finished loading.
    useEffect(() => {
        if (isEditMode && schema) goToStep(2);
    }, [isEditMode, schema]);

    useEffect(() => {
        if (isEditMode) window.dataLayer?.push({ event: 'submittals_edit_form_page_view' });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // When navigating from eform-share with a pre-selected data element, skip to step 2.
    useEffect(() => {
        const preSelectedId = locationState?.preSelectedDataElementId;
        if (!preSelectedId || !provideItems.length) return;
        const de = provideItems.find(item => item.id === preSelectedId);
        if (de) {
            setSelectedDataElement(de);
            goToStep(2);
        }
    }, [locationState?.preSelectedDataElementId, provideItems]);

    // When landing directly on step 2 (via shared link), validate consent and redirect if invalid.
    useEffect(() => {
        if (step !== 2 || !configProducesReady) return;
        checkAllowedReceiver(selectedDataElement?.id).then(hasMatch => {
            if (!hasMatch) {
                addToastMessage('You do not have a valid consent relationship with the intended recipient.', 'error');
                goTo('/submittals');
            }
        });
    }, [step, configProducesReady]);

    const floatingPanelErrorMessage = useMemo(() => {
        return Object.values(errorListToDisplay).flat().map(message => {
            const parts = message.split(/:(.*)/s);
            if (parts.length >= 2) {
                return {
                    message: (
                        <>
                            <strong>{parts[0]}</strong>:{parts[1]}
                        </>
                    )
                };
            }
            return { message };
        });
    }, [errorListToDisplay]);

    const isPendingReview = existingSubmittal?.status === SUBMITTAL_STATUS.PENDING_REVIEW;
    const isRejected = existingSubmittal?.status === SUBMITTAL_STATUS.REJECTED;

    const handleDataElementSelect = (de) => {
        setSelectedDataElement(de);
        window.dataLayer?.push({
            event: 'submittals_step1_data_element_selected',
            data_element_name: de.name || de.id,
        });
    };

    const handleNextStep = () => {
        if (!selectedDataElement) {
            addToastMessage('Please select a data element.', 'error');
            return;
        }
        window.dataLayer?.push({
            event: 'submittals_step1_next',
            data_element_name: selectedDataElement.name || selectedDataElement.id,
        });
        nextStep();
    };

    const handleCancel = () => {
        if (step === 1 && !isEditMode) {
            window.dataLayer?.push({ event: 'submittals_step1_cancel' });
            goTo('/submittals');
            return;
        }
        showCancelModal();
    };

    const handleCancelConfirm = () => {
        hideCancelModal();
        addToastMessage(
            isEditMode ? 'Edit submission cancelled successfully.' : 'New submission cancelled successfully.',
            'success'
        );
        window.dataLayer?.push({ event: 'submittals_step2_cancel' });
        goTo('/submittals');
    };

    const handlePrevious = () => {
        if (isEditMode) return;
        window.dataLayer?.push({ event: 'submittals_step2_previous' });
        prevStep();
    };

    // Show only data elements the org produces AND that are enabled for submittals.
    const availableDataElements = useMemo(() => {
        if (!provideItems.length || !dataElements.length) return [];
        const submittalsIds = new Set(dataElements.map(de => de.dataElementId));
        return provideItems.filter(item => submittalsIds.has(item.id));
    }, [provideItems, dataElements]);

    const filteredDataElements = useMemo(() => {
        const sorted = orderBy(availableDataElements, [d => (d.name || d.id)?.toLowerCase()], ['asc']);
        if (!searchInput) return sorted;
        const lower = searchInput.toLowerCase();
        return sorted.filter(d =>
            (d.name || '')?.toLowerCase().includes(lower) ||
            d.id?.toLowerCase().includes(lower)
        );
    }, [availableDataElements, searchInput]);

    const autoFilledPaths = useMemo(() => getAutoFilledPaths(schema), [schema]);
    const copyFromLastSections = useMemo(() => getCopyFromLastSections(schema), [schema]);
    const lockedPaths = useMemo(
        () => getLockedPaths(schema, existingSubmittal?.status),
        [existingSubmittal, schema]
    );
    const editableHighlightPaths = useMemo(
        () => isSubmittalsEnabled
            ? getEditableHighlightPaths(schema, existingSubmittal?.status)
            : new Set(),
        [isSubmittalsEnabled, schema, existingSubmittal]
    );

    const formHandlerProps = {
        isDirty,
        errorListToDisplay,
        setIsValidFormat,
        isSubmitClicked,
        autoFilledPaths,
        lockedPaths,
        copyFromLastSections,
        editableHighlightPaths,
        onCopyFromLast,
    };

    if (!showSubmittals) return null;

    return (
        <div className="submittals-create-page">
            <Breadcrumbs breadcrumbItems={breadcrumbsItems} />

            <div className="title-header">
                <div className="title-header__title">
                    {isEditMode ? 'Update Submission' : 'Create New Submission'}
                </div>
            </div>

            <Stepper steps={STEPS} currentStep={isEditMode ? 1 : step - 1} />

            <div className="submittals-create-page__content">
                {/* Step 1: Select E-Form (skipped entirely in edit mode) */}
                {step === 1 && !isEditMode && (
                    <div className="submittals-create-page__step1">
                        <div className="submittals-create-page__step1-title">
                            Select Data Element to submit
                        </div>
                        {/* <div className="submittals-create-page__step1-search">
                            <Search
                                value={searchInput}
                                placeholder="Search"
                                onChange={e => setSearchInput(e?.target?.value)}
                                onCancel={() => setSearchInput('')}
                            />
                        </div> */}
                        <div className="submittals-create-page__step1-list" data-testid="data-element-card-view">
                            {filteredDataElements.length > 0 ? (
                                filteredDataElements.map(de => (
                                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                                    <div
                                        className="submittals-create-page__step1-card"
                                        key={de.id}
                                        id="submittals-step1-data-element-card-id"
                                        data-element-name={de.name || de.id}
                                        data-selected={selectedDataElement?.id === de.id ? 'true' : undefined}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleDataElementSelect(de)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleDataElementSelect(de);
                                            }
                                        }}
                                    >
                                        <SelectorBox2
                                            label={de.name || de.id}
                                            active={selectedDataElement?.id === de.id}
                                            radioLeft
                                            infoTooltip={de.schema?.description}
                                        />
                                    </div>
                                ))
                            ) : (
                                <div className="submittals-create-page__step1-empty">
                                    No data elements available.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Loading state: edit mode pre-step-2, or step 2 before schema/payload ready */}
                {((isEditMode && step === 1) || (step === 2 && (!schema || !payload))) && (
                    <div className="submittals-create-page__loading">
                        <Loader />
                    </div>
                )}
                {step === 2 && schema && payload && (
                    <div className="submittals-create-page__step2">
                        {isPendingReview && (
                            <Callout
                                type={CalloutType.WARNING}
                                variant={CalloutVariant.STANDARD}
                                message="Only approver details can be edited after submission. To edit data details, the approver have to reject your submission."
                                className="submittals-create-page__pending-callout"
                            />
                        )}
                        {isRejected && (
                            <SubmittalBanner
                                status={existingSubmittal.status}
                                receiverOrgName={existingSubmittal?.receiver_org_name}
                                latestLog={activityLogs[0] ?? null}
                                onViewActivityLog={activityLogs.length > 0 ? showActivityLog : null}
                            />
                        )}
                        <AccordionCard
                            title={`${selectedDataElement?.name} Details`}
                            defaultOpen
                            className="submittals-data-accordion"
                        >
                            <div className="eform-form">
                                <EFormRenderer
                                    schema={schema}
                                    payload={payload}
                                    setPayload={setPayload}
                                    {...formHandlerProps}
                                    isSubmitClicked={isSubmitClicked}
                                />
                            </div>
                        </AccordionCard>
                    </div>
                )}
            </div>

            {/* Footer Actions */}
            <div className="layout-default__sticky-actions">
                <div className="layout-default__sticky-actions__left">
                    <Button
                        text="Cancel"
                        sizing="Medium"
                        full
                        noborder
                        nobackground
                        nohover
                        id="submittals-step1-cancel-button-id"
                        submit={handleCancel}
                    />
                </div>
                <div className="layout-default__sticky-actions__right">
                    {step === 1 && !isEditMode && (
                        <Button
                            text="Next"
                            sizing="Medium"
                            full
                            round
                            id="submittals-step1-next-button-id"
                            data-element-name={selectedDataElement?.name || selectedDataElement?.id || ''}
                            disabled={!selectedDataElement}
                            submit={handleNextStep}
                        />
                    )}
                    {step === 2 && !isPendingReview && (
                        <>
                            {!isEditMode && (
                                <Button
                                    text="Previous"
                                    sizing="Medium"
                                    full
                                    noborder
                                    nobackground
                                    nohover
                                    submit={handlePrevious}
                                />
                            )}
                            {(!isEditMode || existingSubmittal?.status === 'DRAFT') && (
                                <Button
                                    text={isSaving ? 'Saving...' : 'Save as draft'}
                                    sizing="Medium"
                                    full
                                    noborder
                                    nobackground
                                    nohover
                                    disabled={isSaving || isSubmitting}
                                    submit={handleSaveDraft}
                                />
                            )}
                            <Button
                                text={isSubmitting ? 'Submitting...' : 'Submit'}
                                sizing="Medium"
                                full
                                round
                                disabled={isSaving || isSubmitting}
                                submit={handleSubmit}
                            />
                        </>
                    )}
                    {step === 2 && isPendingReview && (
                        <Button
                            text={isSaving ? 'Saving...' : 'Save & Submit'}
                            sizing="Medium"
                            full
                            round
                            disabled={isSaving}
                            submit={handleUpdateApprover}
                        />
                    )}
                </div>
            </div>

            {/* Error Panel */}
            {floatingPanelErrorMessage.length > 0 && isDirty && (
                <FloatingPanel
                    title={`${floatingPanelErrorMessage.length} errors found`}
                    messages={floatingPanelErrorMessage}
                    type={FloatingPanelType.DESTRUCTIVE}
                    className='eform-floating-panel'
                    ref={floatingPanelRef}
                />
            )}

            <ActivityLogModal
                visible={isActivityLogVisible}
                close={hideActivityLog}
                entries={buildLogEntries(activityLogs)}
            />

            {/* Cancel Confirmation Modal */}
            <Modal visible={isCancelModalVisible} variant="medium" close={hideCancelModal}>
                <CardConfigure>
                    <CardConfigure.Header>
                        <CardConfigure.Header.Title>
                            <div className="card-configure__header__left">
                                <div className="card-configure__title">
                                    <div className="card-configure__title__left">
                                        <div className="card-configure__icon card-configure__icon--error submittals-cancel-modal__icon">
                                            <IconInfo />
                                        </div>
                                    </div>
                                    <div className="card-configure__title__right">
                                        <div className="card-configure__title__right__top">
                                            {isEditMode ? 'Cancel Edit Submission' : 'Cancel New Submission'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardConfigure.Header.Title>
                    </CardConfigure.Header>
                    <CardConfigure.Body>
                        <CardConfigure.Body.Col>
                            <div className="submittals-cancel-modal__body">
                                All progress made will be lost.<br />Proceed anyway?
                            </div>
                        </CardConfigure.Body.Col>
                    </CardConfigure.Body>
                    <CardConfigure.Footer>
                        <CardConfigure.Footer.Left>
                            <Button textWarning noborder nobackground nohover text="Cancel" submit={hideCancelModal} />
                        </CardConfigure.Footer.Left>
                        <CardConfigure.Footer.Right>
                            <Button error round text="Confirm" submit={handleCancelConfirm} />
                        </CardConfigure.Footer.Right>
                    </CardConfigure.Footer>
                </CardConfigure>
            </Modal>

        </div>
    );
};

export default CreateSubmissionPage;
